import React, { useEffect, useRef, useState } from "react";
import { MetaTags } from "@/components/common/Meta";
import { mainPagesSeo } from "@/configs/seo.config";
import Header from "@/components/Layout/Header";
import { Col, Container, Row } from "react-bootstrap";
import { NextPage } from "next";
import styles from "@/styles/pages/software.module.scss";
import { twoDigitsNumber } from "@/utils/helpers";
import { FaArrowRight } from "react-icons/fa";
import { CgArrowRight } from "react-icons/cg";
import Image from "next/image";
import BlueGlow from "@/assets/svg/services/blue-glow.svg";
import GradGlow from "@/assets/svg/services/grad-glow.svg";
import FAQ from "@/components/common/Faq";
import { softwareFaq } from "@/configs/faq.config";
import Link from "next/link";
import dynamic from "next/dynamic";
const Lottie = dynamic(() => import("react-lottie-player"), { ssr: false });
import lottieJson from "@/assets/lottie/software.json";
import { routes } from "@/configs/navlink.config";
import { useMouse } from "@/hooks/useMouse";
import { ServiceCard } from "@/components/common/ServiceCard";
import { BiRightArrowAlt } from "react-icons/bi";
import { useCalendlyModal } from "@/contexts/Calendly";

const subsections = [
  {
    id: 1,
    title: "IT Consulting Services",
    content:
      "Partner with Metachain's seasoned expert team and gain more than just IT consulting. With our rich industry knowledge, we ensure your software doesn't just succeed, it leads.",
    link: routes.itConsulting,
  },

  {
    id: 2,
    title: "Custom Software Development",
    content:
      "Why settle for generic when you can have custom-made? Our software services mirror the precision of a master tailor, aligning perfectly with your brand's identity, ensuring a balance of beauty, function, and durability. ",
    link: routes.customSoftware,
  },
  {
    id: 3,
    title: "Mobile Apps Development",
    content:
      "Bridge the gap between your brand and the global audience. Whether it's the elegance of iOS or the expanse of Android, Metachain ensures your story resonates on every mobile platform. ",
    link: routes.mobile,
  },
  {
    id: 4,
    title: "Web Development",
    content:
      "The digital realm is ever-changing, and Metachain is at its top. Our web solutions, from intricate front-end designs to robust back-end systems, are future-proof, positioning your brand at the digital vanguard. ",
    link: routes.web,
  },
  {
    id: 5,
    title: "Developers Recruitment",
    content:
      "Success hinges on the right talent. Connect with top-tier Full Stack Developers, AI Specialists, Blockchain Gurus, and more through our recruitment services.",
    link: routes.recruitment,
  },
];

const ServicePage: NextPage = () => {
  const { ref, ...position } = useMouse();
  const { toggleModal } = useCalendlyModal();

  return (
    <>
      <MetaTags {...mainPagesSeo["software"]} />

      <Container fluid>
        <Header transparent />
        <Row
          className={` justify-content-center mb-mobile-4rem sec-py mt-md-5 text-center text-md-start`}
        >
          <Col xs={11} md={5} lg={6} className='my-auto'>
            <h1 className='bold mb-3'>
              <span className='text-gradient'>Software Development Services</span> <br />
              Faster, Smarter, and Better
            </h1>
            <p className='pe-0 pe-md-5'>
              Dive into unmatched speed with Metachain Technologies, Canada's leading name in
              software solutions and innovation. We're shaping tomorrow's software today.
            </p>

            <div
              className='d-flex hero-action gap-2 justify-content-center justify-content-md-start'
              onClick={toggleModal}
            >
              <button className='btn btn-primary home-btn'>Book a Call</button>
              <span className='arr-right-btn d-flex align-items-center justify-content-center position-relative'>
                <BiRightArrowAlt />
                <BiRightArrowAlt className='position-absolute' />
              </span>
            </div>
          </Col>
          <Col
            xs={{ span: 11, order: "first" }}
            md={{ span: 5, order: "last" }}
            lg={4}
            className='mb-3 mb-md-0'
          >
            <Lottie loop={false} animationData={lottieJson} play speed={1} />
          </Col>
        </Row>
      </Container>

      <Container fluid className='position-relative mt-20' ref={ref}>
        <Row className='justify-content-center mb-mobile-4rem '>
          <Col xs={10} md={10}>
            <Row className='justify-content-center'>
              <h2 className='text-center bold fs-1 col-md-9 mb-5 '>
                Our Diverse Range of Software Development Solutions
              </h2>
              {subsections.map((section, ix) => (
                <Col xs={12} md={6} lg={4} className='mb-4 text-center' key={section.title}>
                  <ServiceCard section={section} position={position} index={ix} />
                </Col>
              ))}
            </Row>
            <Row className='my-20'>
              <Col md={7} className='mb-4 pe-5 d-flex flex-column justify-content-between  '>
                <div>
                  <h2 className='bold fs-1 mb-4'>Developers Recruitment: Build your dream team</h2>
                  <p>
                    The right talent is the cornerstone of success. Our Developers Recruitment
                    services connect you with Full Stack Developers, AI Engineers, Blockchain
                    Experts, and more, ensuring that you have the perfect ensemble to bring your
                    vision to life.
                  </p>
                </div>

                <div className='text-white d-flex align-items-center gap-2  '>
                  Hiring Developers?
                  <div
                    onClick={toggleModal}
                    className=' d-flex align-items-center read-more text-primary cursor-pointer'
                  >
                    Click Here
                    <span className='position-relative d-flex justify-content-center align-items-center'>
                      <CgArrowRight className='ms-1 my-auto text-primary' />
                      <CgArrowRight className='ms-1 my-auto position-absolute' />
                    </span>
                  </div>
                </div>
              </Col>
              <Col md={5} className='text-white'>
                <h3 className='bold mb-4'>
                  The Metachain Promise: Crafting Legacies, Not Just Software.
                </h3>
                <p className='text-white'>The Hallmarks of Metachain</p>
                <ul className='d-flex flex-column gap-3'>
                  <li>Seasoned Professional Developers.</li>
                  <li>State-of-the-Art Technology.</li>
                  <li>A Relationship Beyond Business.</li>
                  <li>Cost-Effective Excellence.</li>
                </ul>
              </Col>
            </Row>
            <Image src={GradGlow} alt='grad-glow' className='grad-glow position-absolute left-0' />
            <Row className={`${styles.advContainer} text-center `}>
              <p className='w-75'>
                From the initial spark of an idea to the final deployment, our services encompass
                the entire lifecycle of software development, where every aspect is fine-tuned to
                perfection.
              </p>
              <div>
                <h2 className='bold'>Chart Your Digital Odyssey with Metachain.</h2>
                <p>
                  Get in touch with us today and discover how Metachain Technologies can redefine
                  the way you do business.
                </p>
              </div>
            </Row>
            <Image src={BlueGlow} alt='blue-glow' className='blue-glow position-absolute' />
          </Col>
        </Row>
        <Row className='justify-content-center'>
          <Col xs={10} md={10} xl={8}>
            <FAQ data={softwareFaq} />
          </Col>
          <Image
            src={GradGlow}
            alt='grad-glow '
            className='grad-glow sec position-absolute left-0'
          />
        </Row>
      </Container>
    </>
  );
};

export default ServicePage;
